import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox, Dialog, DialogContent, DialogTitle } from '@mui/material';
import * as z from 'zod';

import { type CreateModelInput } from '../../api/models';
import Button from '../../common/Button';
import { useCreateModel, useUpdateModel } from '../../hooks';
import { useGetDatasets } from '../../hooks/tanstack/useDatasetsHook';
import { useGetModels } from '../../hooks/tanstack/useModelsHook';
import { useGetRouteParams } from '../../hooks/tanstack/useUtilsHook';
import { handleQueryError } from '../../utils/api';
import FormInput from '../form/FormInput';
import FormSelect from '../form/FormSelect';

const CreateModelSchema = z.object({
  name: z.string().min(1, "Model name can't be empty").trim(),
  description: z.string().trim().optional(),
  dataset: z.number(),
  copy_scenarios_from: z.number().or(z.string()).nullable().optional(),
  isCreateNew: z.boolean().default(false),
  new_dataset_name: z.string().trim().optional(),
  new_dataset_description: z.string().trim().optional(),
});

type CreateModelSchemaType = z.infer<typeof CreateModelSchema>;

type CreateModelFormDialogProps = {
  open: boolean;
  title: string;
  defaultValues?: {
    id: number;
    name: string;
    description: string;
    dataset: number;
  };
  handleCloseDialog: () => void;
};

const CreateModelFormDialog = ({
  open,
  title,
  handleCloseDialog,
  defaultValues,
}: CreateModelFormDialogProps) => {
  const [error, setError] = useState();

  const { projectId, orgId, modelId: routeModelId } = useGetRouteParams();

  const modelId = defaultValues?.id || routeModelId;

  const [searchParams, setSearchParams] = useSearchParams();

  const { data: datasets } = useGetDatasets(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!orgId && !!projectId,
    }
  );

  const readyDatasets = useMemo(
    () => datasets?.filter((d) => d.is_data_ready),
    [datasets]
  );

  const { data: models } = useGetModels(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const form = useForm<CreateModelSchemaType>({
    resolver: zodResolver(CreateModelSchema),
    defaultValues: defaultValues
      ? {
          name: defaultValues.name,
          description: defaultValues.description,
          dataset: defaultValues.dataset,
        }
      : {
          name: '',
          description: '',
          dataset: readyDatasets?.[0]?.id,
        },
  });

  const {
    handleSubmit,
    watch,
    reset,
    register,
    formState,
    setError: setFormError,
    setValue,
  } = form;

  const { isDirty } = formState;

  const isCreateNewDataset = watch('isCreateNew');

  useEffect(() => {
    if (defaultValues && !isDirty) {
      reset(defaultValues);
    }
  }, [defaultValues]);

  useEffect(() => {
    if (readyDatasets?.[0] && !defaultValues) {
      setValue('dataset', readyDatasets[0].id);
    }
  }, [readyDatasets, defaultValues, open]);

  const { mutate: createModel, isPending: isCreatingModel } = useCreateModel({
    onSuccess(data) {
      searchParams.set('model_id', data.id?.toString());

      setSearchParams(searchParams);

      handleCloseDialog();
      reset();
    },
    onError(error) {
      handleQueryError(error, setError);
    },
  });

  const { mutate: updateModel, isPending: isUpdatingModel } = useUpdateModel({
    onSuccess() {
      handleCloseDialog();
      reset();
    },
    onError(error) {
      handleQueryError(error, setError);
    },
  });

  useEffect(() => {
    if (!error) {
      return;
    }

    Object.keys(error as unknown as Object).forEach((key) =>
      setFormError(key as keyof CreateModelSchemaType, {
        message: error?.[key] as unknown as string,
      })
    );
  }, [error]);

  const onSubmit: SubmitHandler<CreateModelSchemaType> = async () => {
    const watchData = watch();

    const {
      name,
      description,
      dataset,
      new_dataset_name,
      new_dataset_description,
      copy_scenarios_from,
      isCreateNew,
    } = watchData;

    if (!dataset) {
      return;
    }

    const data = {
      name,
      description,
      dataset,
      project: projectId,
      copy_dataset: isCreateNew,
      new_dataset_name: isCreateNew ? new_dataset_name : undefined,
      new_dataset_description: isCreateNew
        ? new_dataset_description
        : undefined,
    } as CreateModelInput['data'];

    if (Number(copy_scenarios_from)) {
      data['copy_scenarios_from'] = Number(copy_scenarios_from);
    }

    if (defaultValues) {
      updateModel({
        orgId,
        projectId,
        modelId,
        data: {
          ...data,
        },
      });
      return;
    }

    createModel({
      orgId,
      projectId,
      data,
    });
  };

  const isDisabled = isUpdatingModel || isCreatingModel;

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleCloseDialog();
        reset();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className="w-full">
        <h2 className="pt-2 text-2xl font-bold">{title}</h2>
      </DialogTitle>
      <DialogContent>
        <div>
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="mb-2 space-y-4">
              <div className="space-y-4">
                <>
                  <FormInput
                    label="Model Name"
                    name="name"
                    inputProps={{
                      maxLength: 255,
                    }}
                  />
                  <FormInput
                    label="Model Description"
                    name="description"
                    placeholder="(Optional)"
                    multiline
                    rows={5}
                  />
                </>
                {models && models?.length > 0 && !defaultValues && (
                  <FormSelect
                    label="Copy scenario from another model"
                    name="copy_scenarios_from"
                    defaultValue={'none'}
                    options={[
                      {
                        value: 'none',
                        label: "Don't copy (BASE)",
                      },
                      ...models
                        .filter((model) => !model.is_base)
                        .map((model) => ({
                          value: model.id,
                          label: model.name,
                        })),
                    ]}
                  />
                )}
                {readyDatasets && !defaultValues && (
                  <FormSelect
                    label="Select Dataset"
                    name="dataset"
                    defaultValue={readyDatasets[0]?.id}
                    options={readyDatasets.map((dataset) => ({
                      value: dataset.id,
                      label: dataset.name,
                    }))}
                  />
                )}
                {!defaultValues && (
                  <div className="flex items-center gap-1">
                    <Checkbox {...register('isCreateNew')} />
                    <p>Create new dataset base on this dataset.</p>
                  </div>
                )}
                {isCreateNewDataset && (
                  <>
                    <FormInput
                      label="Dataset Name"
                      name="new_dataset_name"
                      required
                    />
                    <FormInput
                      label="Dataset Description"
                      name="new_dataset_description"
                      multiline
                      placeholder="(Optional)"
                      rows={5}
                    />
                  </>
                )}
              </div>
              <div className="flex-end flex justify-end gap-3">
                <Button
                  onClick={() => {
                    handleCloseDialog();
                    reset();
                  }}
                  variant="outlined"
                  disabled={isDisabled}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={isDisabled}
                  variant="contained_blue"
                  disabled={isDisabled}
                >
                  {defaultValues ? 'Save' : 'Create'}
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateModelFormDialog;
